import React, { useEffect, useMemo, useState } from "react";
import {
  FaQuestionCircle,
  FaCog,
  FaUser,
  FaInfoCircle,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { IoIosArrowForward, IoIosMenu } from "react-icons/io";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { IconButton, Button, Tooltip } from "@mui/material";
import dataService from "../../../services/data.service";
import { useHMG } from "../../../HMGContext"; // Import the context hook
import LogoutButton from "../../config/LogoutButton";
import ApartmentIcon from '@mui/icons-material/Apartment';
import ViewAsDialog from "../dialogs/ViewAsDialog";
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import { exactMatchRoute, matchesRoute, getPageType } from "../../../config/routeConfig";
import { canAccessDataMigration, canAccessPairedDailyDedup } from "../../../config/dataMigrationAccess";
import { useTpxPermissions } from "../../../contexts/TpxPermissionsContext";
import { PRODUCT_SYSTEM_IDS, PRODUCT_SYSTEM_NAMES } from "../../../constants/tpxApplication";

const CRITIQUE_MENU_CHILDREN = [
  { name: "Critique templates", path: "/critique/templates" },
  { name: "All critiques", path: "/critique/all" },
];

// TBC: Platform Home dev/uat use the prod URL
const PRODUCT_URLS = {
  development: {
    platformHome: "https://gen2uat.mydigitaloffice.com/", // using UAT platform's URL for dev environment
    intelliSight: "https://dev.intellisight.otelier.io",
    rec: "https://dev.rec.otelier.io",
    digiOffice: "https://gen2uatmd.mydigitaloffice.com/",
  },
  uat: {
    platformHome: "https://gen2uat.mydigitaloffice.com/",
    intelliSight: "https://uat.intellisight.otelier.io",
    rec: "https://uat.rec.otelier.io",
    digiOffice: "https://gen2uatmd.mydigitaloffice.com/",
  },
  prod: {
    platformHome: "https://launch2.mydigitaloffice.com",
    intelliSight: "https://intellisight.otelier.io",
    rec: "https://rec.otelier.io",
    digiOffice: "https://mydocs2.mydigitaloffice.com/",
  },
};
const productUrls = PRODUCT_URLS[process.env.REACT_APP_ENV] || PRODUCT_URLS.prod;

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(() => {
    const savedState = sessionStorage.getItem('sidebarState');
    // Handle all possible cases:
    if (savedState === null) {
      return true; // Default open on first visit
    }
    if (savedState === 'logout') {
      return true; // Open after logout
    }
    if (savedState === 'true' || savedState === 'false') {
      return savedState === 'true'; // Convert string to boolean
    }
    return true; // Fallback to open
  });
  const [openDropdown, setOpenDropdown] = useState(null); // For handling dropdowns
  const [openPlanningDropdown, setOpenPlanningDropdown] = useState(null); // For handling planning dropdowns
  const [openToolsDropdown, setOpenToolsDropdown] = useState(null); // For handling tool dropdown
  const [hoveredItem, setHoveredItem] = useState(null); // NEW: For handling tooltips for non-dropdown items
  const [userData, setUserData] = useState(null);
  const [firstName, setFirstName] = useState("User");
  const [lastName, setLastName] = useState("");
  const [viewAsDialogOpen, setViewAsDialogOpen] = useState(false);
  const [selectedHMG, setselectedHMG] = useState(() => {
    const savedHmgId = dataService.getSelectedHMGId();
    const hmgList = getHmgData();

    if (savedHmgId) {
      const savedHmg = hmgList.find(hmg => String(hmg.id) === String(savedHmgId));
      if (savedHmg) return savedHmg;
    }

    return hmgList[0];
  });
  const userEmail = dataService.getUserEmail();
  const shouldShowViewAs = userEmail && userEmail.includes('otelier.io');
  const shouldShowCritique = userEmail;
  const shouldShowDataMigration = canAccessDataMigration(userEmail);
  const shouldShowPairedDailyDedup = canAccessPairedDailyDedup(userEmail);

  const location = useLocation();
  const { setSelectedHMG, setIsSidebarOpen, viewAsUserOpen , setCurrentModalName } = useHMG();
  const { planView, coaView, planningReportView, settingsView, critiqueView, critiqueTemplateView } = useTpxPermissions();
  const navigate = useNavigate();
  const powerBiUrls = {
    development: "https://app.powerbi.com/groups/9b64c057-fadf-4ced-af76-5174c5c3fb2c/list",
    uat: "https://app.powerbi.com/groups/34a4970e-58a3-4d9d-8827-5d3149e61857/list",
    prod: "https://app.powerbi.com/groups/d5fa1789-79f3-4076-8651-7043e434622b/list",
  };

  useEffect(() => {
    // Listen for custom HMG change events from the same tab
    const handleHmgChange = (e) => {
      const newHmg = e.detail;
      if (newHmg && newHmg.id !== selectedHMG.id) {
        setselectedHMG(newHmg);
      }
    };

    window.addEventListener('hmgChanged', handleHmgChange);

    return () => {
      window.removeEventListener('hmgChanged', handleHmgChange);
    };
  }, [selectedHMG]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const user = await dataService.fetchUserMeta();
        setUserData(user); // Update state with fetched user data
        // console.log("user :", user)
      } catch (error) {
        console.error("Failed to fetch user data:", error);
        setUserData({ firstName: "User", lastName: "" }); // Fallback in case of error
      }
    };
  
    fetchUserData();
  }, []);

  const productAccess = userData?.productAccess || [];
  const productAccessIds = new Set(productAccess.map((system) => system.id));
  const hasSystemName = (name) =>
    productAccess.some((system) => system.name?.toLowerCase().includes(name));

  // Entries without systemId/systemName are always shown.
  const productsList = [
    {
      name: "Platform Home",
      url: productUrls.platformHome,
      target: "_blank",
      id: "1",
      icon: "/platform_home_circle_logo.svg"
    },
    {
      name: "IntelliSight",
      url: productUrls.intelliSight,
      target: "_blank",
      id: "2",
      icon: "/intellisight_circle_logo.svg",
      systemId: PRODUCT_SYSTEM_IDS.INTELLISIGHT
    },
    {
      name: "Rec",
      url: productUrls.rec,
      target: "_blank",
      id: "3",
      icon: "/rec_circle_logo.svg",
      systemId: PRODUCT_SYSTEM_IDS.REC
    },
    {
      name: "DigiAudit",
      url: productUrls.digiOffice,
      target: "_blank",
      id: "4",
      icon: "/digiaudit_circle_logo.svg",
      systemId: PRODUCT_SYSTEM_IDS.DIGIAUDIT
    },
    {
      name: "DigiPay",
      url: productUrls.digiOffice,
      target: "_blank",
      id: "5",
      icon: "/digipay_circle_logo.svg",
      systemName: PRODUCT_SYSTEM_NAMES.DIGIPAY
    },
  ].filter((product) => {
    if (product.systemId) return productAccessIds.has(product.systemId);
    if (product.systemName) return hasSystemName(product.systemName);
    return true;
  });

  const toggleSidebar = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    setIsSidebarOpen(newState);
    setOpenDropdown(null);  
    setHoveredItem(null);
    // Store as string 'true' or 'false' instead of JSON
    sessionStorage.setItem('sidebarState', String(newState));
  };

  useEffect(() => {
    const savedState = sessionStorage.getItem('sidebarState');
    if (savedState === 'logout') {
      setIsOpen(true);
      setIsSidebarOpen(true);
      sessionStorage.setItem('sidebarState', 'true');
    } else if (savedState !== null) {
      const initialOpen = savedState === 'true';
      setIsOpen(initialOpen);
      setIsSidebarOpen(initialOpen);
    }
  }, []);

  const toggleDropdown = (index) => {
    setOpenDropdown(openDropdown === index ? null : index);
  };

  const toggleToolsDropdown = (index) => {
    setOpenToolsDropdown(openToolsDropdown === index ? null : index);
  };

  function getHmgData() {
    let list = dataService.getHMGList()?.map(hmg => {
      return {
        name: hmg.name,
        id: hmg.id,
        value: hmg.value,
        label: hmg.label,
        dateFormat: hmg.dateFormat || null,
      }
    })

    return list;
  }

  const menuItemsOne = [
    {
      name: selectedHMG.name,
      icon: "solar:buildings-2-bold-duotone",
      children: getHmgData()
    },
  ];

  // Function to set the selected HMG
  const handleHMGSelection = (hmg) => {
    setSelectedHMG(hmg); // Update the selected HMG state
    setselectedHMG(hmg);
    dataService.setSelectedHMGId(hmg.id); // Store the selected HMG ID in your service
    setIsOpen(false);
    setIsSidebarOpen(false);
    setOpenDropdown(null);
    sessionStorage.setItem('sidebarState', 'false');
    // Navigate to home if currently on a plan version page
    if (getPageType(location.pathname) === 'planVersion') {
      navigate("/")
    }
  };

  // Handle View As click
  const handleViewAsClick = () => {
    setViewAsDialogOpen(true);
    setCurrentModalName("View as");
  };

  const isCritiqueActive = CRITIQUE_MENU_CHILDREN.some(
    (child) => child.path && matchesRoute(location.pathname, child.path)
  );

  const menuItemsTwo = useMemo(() => {
    const items = []
    if (planView) {
      items.push(
        {
          name: "Plans",
          path: "/",
          icon: (
            <Icon
              icon="solar:calculator-bold-duotone"
              width={24}
              height={24}
              className={`${exactMatchRoute(location.pathname, "/") ? "!text-[#118D57]" : "!text-[#919EAB]"}`}
            />
          ),
        }
      )
    }

    if (coaView) {
      items.push({
        name: "Chart of accounts",
        path: "/ChartOfAccounts",
        icon: (
          <Icon
            icon="solar:book-bookmark-bold-duotone"
            width={24}
            height={24}
            className={`${exactMatchRoute(location.pathname, "/ChartOfAccounts") ? "!text-[#118D57]" : "!text-[#919EAB]"}`}
          />
        ),
      });
    }

    if (planningReportView) {
      items.push({
        name: "Exports",
        path: "/exports",
        icon: (
          <Icon
            icon="solar:chart-square-bold-duotone"
            width={24}
            height={24}
            className={`${exactMatchRoute(location.pathname, "/exports") || exactMatchRoute(location.pathname, "/export") ? "!text-[#118D57]" : "!text-[#919EAB]"}`}
          />
        ),
      });
    }

    if (shouldShowCritique && (critiqueView || critiqueTemplateView)) {
      const critiqueChildren = CRITIQUE_MENU_CHILDREN.map((child) => {
        if (child.path === "/critique/templates" && !critiqueTemplateView) {
          return {
            ...child,
            disabled: true,
            disabledTooltip: "You don't have permission",
          };
        }
        if (child.path === "/critique/all" && !critiqueView) {
          return {
            ...child,
            disabled: true,
            disabledTooltip: "You don't have permission",
          };
        }
        return child;
      });

      items.push({
        name: "Critique",
        children: critiqueChildren,
        icon: (
          <Icon
            icon="solar:document-add-bold-duotone"
            width={24}
            height={24}
            className={`${isCritiqueActive ? "!text-[#118D57]" : "!text-[#919EAB]"}`}
          />
        ),
      });
    }

    return items;
  }, [planView, coaView, planningReportView, shouldShowCritique, critiqueView, critiqueTemplateView, location.pathname, isCritiqueActive]);

  // NEW: Use the userData state instead of directly calling dataService.getUser()
  useEffect(() => {
    if (userData) {
      setFirstName(userData.firstname || userData.firstName  || "User");
      setLastName(userData.lastname || userData.lastName || "");
    }
  }, [userData]);
  const capitalizedFirstName = firstName.charAt(0).toUpperCase() + firstName.slice(1);
  const user = lastName
    ? `${capitalizedFirstName} ${lastName.charAt(0).toUpperCase()}.`
    : capitalizedFirstName;

  // Get user email and check if it contains "otelier.io"
  const menuItemsThree = useMemo(() => [
    ...(settingsView
      ? [
          {
            name: "Settings",
            path: "/settings",
            redirectToPage: true,
            icon: (
              <Icon
                icon="solar:settings-bold-duotone"
                width={23}
                height={23}
              />
            ),
            children: [
              // { name: "Setting A", path: "" },
              // { name: "Setting B", path: "" },
            ]
      
    },
  ]:[]),
    ...(shouldShowDataMigration
      ? [
          {
            name: "Data migration",
            path: "/data-migration",
            redirectToPage: true,
            icon: (
              <Icon
                icon="solar:cloud-upload-bold-duotone"
                width={24}
                height={24}
                className={`${exactMatchRoute(location.pathname, "/data-migration") ? "!text-[#118D57]" : "!text-[#919EAB]"}`}
              />
            ),
            children: [],
          },
        ]
      : []),
    ...(shouldShowPairedDailyDedup
      ? [
          {
            name: "Paired-daily dedup",
            path: "/paired-daily-dedup",
            redirectToPage: true,
            icon: (
              <Icon
                icon="solar:broom-bold-duotone"
                width={24}
                height={24}
                className={`${exactMatchRoute(location.pathname, "/paired-daily-dedup") ? "!text-[#118D57]" : "!text-[#919EAB]"}`}
              />
            ),
            children: [],
          },
        ]
      : []),
    // Conditionally include "View as" menu item
    ...(shouldShowViewAs ? [{
      name: "View as",
      // path: "",
      redirectToPage: false,
      icon: (
        <Icon
          icon="solar:glasses-bold-duotone"
          width={23}
          height={23}
          style={{ color: "#637381" }}
          className="!text-[#919EAB]"
        />
      ),
      children: [],
      disable: viewAsUserOpen
    }] : []),
    {
      name: user,
      icon: (
        <Icon
          icon="solar:user-circle-bold-duotone"
          width={23}
          height={23}
          style={{ color: "#637381" }}
          className="!text-[#919EAB]"
        />
      ),
      children: [
        // { name: "Reset Password", path: "" },
        { name: "Log out", path: "logout" },
      ],
    },
  ], [settingsView, user]);

  return (
    <>
      <div className="flex bg-white shadow-md z-[50]">
        <div className={`sidebar-container  ${isOpen ? "w-[280px]" : "w-[84px]"}`}>
          <div
            className={`sidebar-fixed fixed transition-all duration-300  flex flex-col z-50 bg-white ${isOpen ? "justify-start p-4" : "p-1.5"
              }`}
            style={{ width: "inherit" }}
          >
            <div className="space-y-1">
              <div className={`flex items-center ${isOpen ? "min-h-[52px]" : "min-h-[62px] justify-center"}`}>
                <IconButton
                    onClick={toggleSidebar}
                >
                  {/* <IoIosMenu
                  onClick={toggleSidebar}
                  className="cursor-pointer text-gray-600"
                  size={26}
                /> */}
                  <Icon
                    icon="solar:hamburger-menu-linear"
                    className="cursor-pointer text-gray-500"
                  />
                </IconButton>
              </div>

              <div
                key="TruePlan"
                onMouseEnter={() => !isOpen && setOpenDropdown(1)} // Open dropdown on hover
                onMouseLeave={() => !isOpen && setOpenDropdown(null)} // Close dropdown when hover out
                className="relative !mt-0"
              >
                <div
                  className={`menu-item flex items-center gap-1 ${isOpen ? "justify-between py-1 " : "justify-start py-2"
                    } px-4 rounded-[10px] cursor-pointer ${"menu-active text-[#118D57] font-bold"}`}
                  onClick={() => toggleDropdown(1)} // Toggle dropdown
                  onFocus={() => !isOpen && setOpenDropdown(1)} // Show on hover in collapsed mode
                  onBlur={() => !isOpen && setOpenDropdown(null)} // Hide on hover out
                >
                  <div className="flex gap-2 flex-shrink-0 min-w-[24px]">
                    <img
                      src="/tpx_circle_logo.svg"
                      alt="TruePlan"
                      width={24}
                      height={24}
                      className="rounded-full !w-[24px] !h-[24px] flex-shrink-0"
                      style={{ width: '24px', height: '24px', flexShrink: 0 }}
                    />
                    {isOpen && (
                      <span
                        className={`menu-text transition-all duration-300 text-[#118D57] text-sm leading-[22px] font-semibold ${isOpen ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
                          }`}
                      >
                        TruePlan
                      </span>
                    )}
                  </div>
                  {/* Show a dropdown icon if the item has children */}
                  {productsList && productsList.length > 0 && (
                    <span className="ml-auto">
                      {openDropdown === 1 ? (
                        isOpen ? (
                          <Icon
                            icon="eva:arrow-ios-upward-fill"
                          />
                        ) : (

                          <IoIosArrowForward />
                        )
                      ) : isOpen ? (
                        <Icon
                          icon="eva:arrow-ios-downward-fill"
                        />
                      ) : (
                        <IoIosArrowForward />

                      )}
                    </span>
                  )}
                </div>

                {/* Dropdown section for child items */}
                {openDropdown === 1 && productsList && productsList.length > 0 && (
                  <div
                    className={`dropdown-menu ${isOpen
                      ? "block transition-all duration-300"
                      : "absolute left-[72px] top-[-7px] bg-white/[.98] p-2 py-2 shadow-lg rounded-lg w-max flex flex-col gap-2 transition-all duration-300" // Add flex & column styles
                      } ${openDropdown === 1 ? "open" : ""}`}
                  >
                    {productsList.map((child) => (
                      child.url ? (
                        <a
                          href={child.url}
                          target={child.target}
                          rel="noopener noreferrer"
                          key={child.name}
                          className={`flex items-center gap-2 p-1 px-4 w-full rounded-lg cursor-pointer text-sm leading-[22px] !text-[#637381] hover:bg-[#F6F7F8] ${child.path && exactMatchRoute(location.pathname, child.path)
                            ? "menu-child-active text-[#118D57] font-bold py-2 px-2"
                            : ""
                            } ${isOpen ? "pl-[22px] mt-1" : "p-1"}`}
                        >
                          {child.icon && (
                            <img 
                              src={`${child.icon}`} 
                              alt={child.name}
                              width={20}
                              height={20}
                              className="flex-shrink-0 rounded-full"
                              style={{ width: 20, height: 20 }}
                            />
                          )}
                          {child.name}
                        </a>
                      ) : (
                        <Link
                          to={child.path}
                          key={child.name}
                          className={`flex items-center gap-2 p-2.5 rounded-lg cursor-pointer hover:bg-[#1c252e0d] ${child.path && exactMatchRoute(location.pathname, child.path)
                            ? "menu-child-active text-[#118D57] font-bold"
                            : ""
                            }`}
                        >
                          {child.icon && (
                            <img 
                              src={`${child.icon}`} 
                              alt={child.name}
                              width={20}
                              height={20}
                              className="flex-shrink-0 rounded-full"
                              style={{ width: 20, height: 20 }}
                            />
                          )}
                          {child.name}
                        </Link>
                      )
                    ))}
                  </div>
                )}
              </div>
              {menuItemsOne.map((item, index) => (
                <div
                  key={item.name}
                  onMouseEnter={() => !isOpen && setOpenDropdown(index)} // Open dropdown on hover
                  onMouseLeave={() => !isOpen && setOpenDropdown(null)} // Close dropdown when hover out
                  className="relative"
                >
                  <div
                    className={`menu-item flex items-center hover:bg-[#919EAB14]  gap-1 ${isOpen ? "justify-between p-1" : "justify-start py-2"
                      }  px-4 rounded-[10px] cursor-pointer ${selectedHMG.id == item.id
                        ? "menu-active text-[#118D57] font-bold"
                        : ""
                      }`}
                    onClick={() => toggleDropdown(index)} // Toggle dropdown
                    onFocus={() => !isOpen && setOpenDropdown(index)} // Show on hover in collapsed mode
                    onBlur={() => !isOpen && setOpenDropdown(null)} // Hide on hover out
                  >
                    <div className="flex items-center gap-2">
                      <Icon
                        icon={item.icon}
                        className="!text-[#919EAB] !w-[24px] !h-[24px]"
                      />
                      {isOpen && (
                        <span
                          className={`transition-opacity duration-300 text-[#212B36] font-normal text-sm leading-[22px] ${isOpen ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
                            }`}
                        >
                          {item.name}
                        </span>
                      )}
                    </div>
                    {/* Show a dropdown icon if the item has children */}
                    {item.children && item.children.length > 0 && (
                      <span className="ml-auto text-[#637381]">
                        {openDropdown === index ? (
                          isOpen ? (
                            <Icon
                              icon="eva:arrow-ios-upward-fill"
                            />
                          ) : (
                            <IoIosArrowForward />
                          )
                        ) : isOpen ? (
                          <Icon
                            icon="eva:arrow-ios-downward-fill"
                          />
                        ) : (
                          <IoIosArrowForward />
                        )}
                      </span>
                    )}
                  </div>

                  {/* Dropdown section for child items */}
                  {openDropdown === index && item.children && item.children.length > 0 && (
                    <div
                      className={`dropdown-menu ${isOpen
                        ? "block transition-all duration-300 !max-h-[calc(100vh-540px)] !overflow-auto"
                        : "absolute left-[72px] top-[-22px] bg-white/[.98] p-2 shadow-lg rounded-[10px] justify-start w-max flex flex-col gap-1 transition-all duration-300 !max-h-[250px] !overflow-auto" // Add flex & column styles
                        } ${openDropdown === index ? "open" : ""}`}
                    >
                      {item.children.map((child) => (
                        <Button
                          sx={{
                            '&:hover': {
                              backgroundColor: "#1c252e0d",
                            },
                          }}
                          key={child.name}
                          className={`!py-1  flex !justify-start !items-start rounded-[10px] cursor-pointer w-full !mt-1 !text-start text-sm leading-[22px] hover:bg-[#919EAB14] ${isOpen ? "!ps-[48px]" : ""} ${selectedHMG.id == child.id
                            ? "menu-child-active !text-[#118D57] !bg-[#E1F4E5] !font-semibold"
                            : "!text-[#637381] !font-normal"
                            }`}
                          onClick={() =>{ 
                            handleHMGSelection(child)
                            setOpenDropdown(null)
                          }}
                        >
                          {child.name}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              <div className={`${isOpen ? "hidden" : "block"} border border-[#919EAB33] w-[24px] mx-auto !my-[32px]`}></div>

              <div className="space-y-1 !mt-0">
                {/* Planning Section Header */}
                {isOpen && (
                  <div className="pl-4 font-bold uppercase text-xs bg-gradient-to-r from-[#a4de48] to-[#5ec1b0] inline-block text-transparent bg-clip-text !mt-6 mb-2">
                    Planning
                  </div>
                )}
                {menuItemsTwo.map((item, index) => {
                  const hasChildren = item.children && item.children.length > 0;
                  const childActive = hasChildren && item.children.some((child) => child.path && matchesRoute(location.pathname, child.path));
                  const isActive = item.path
                    ? (item.path === "/exports"
                        ? (exactMatchRoute(location.pathname, "/exports") || exactMatchRoute(location.pathname, "/export"))
                        : exactMatchRoute(location.pathname, item.path))
                    : childActive;

                  if (hasChildren) {
                    const isDropdownOpen = openPlanningDropdown === index;
                    return (
                      <div
                        key={item.name}
                        className="relative"
                        onMouseEnter={() => {
                          if (!isOpen) {
                            setOpenPlanningDropdown(index);
                          }
                        }}
                        onMouseLeave={() => {
                          if (!isOpen) {
                            setOpenPlanningDropdown(null);
                          }
                        }}
                      >
                        <div
                          className={`menu-item flex items-center hover:bg-[#919EAB14] gap-1 ${isOpen ? "justify-between p-1" : "justify-start ps-6 py-2"
                            } px-4 rounded-[10px] cursor-pointer ${isActive ? "menu-active text-[#118D57] font-semibold" : "text-[#212B36]"}`}
                          onClick={() => setOpenPlanningDropdown(isDropdownOpen ? null : index)}
                          onFocus={() => !isOpen && setOpenPlanningDropdown(index)}
                          onBlur={() => !isOpen && setOpenPlanningDropdown(null)}
                        >
                          <div className="flex items-center gap-2">
                            {item.icon}
                            {isOpen && (
                              <span
                                className={`transition-opacity duration-300 text-sm leading-[22px]  ${isOpen
                                  ? "opacity-100 translate-x-0"
                                  : "opacity-0 -translate-x-4"
                                  }`}
                              >
                                {item.name}
                              </span>
                            )}
                          </div>
                          <span className="ml-auto text-[#637381]">
                            {isDropdownOpen ? (
                              isOpen ? (
                                <Icon icon="eva:arrow-ios-upward-fill" />
                              ) : (
                                <IoIosArrowForward />
                              )
                            ) : isOpen ? (
                              <Icon icon="eva:arrow-ios-downward-fill" />
                            ) : (
                              <IoIosArrowForward />
                            )}
                          </span>
                        </div>

                        {isDropdownOpen && (
                          <div
                            className={`dropdown-menu ${isOpen
                              ? "block transition-all duration-300 !max-h-[calc(100vh-540px)] !overflow-auto"
                              : "absolute left-[72px] top-[-22px] bg-white/[.98] p-2 shadow-lg rounded-[10px] justify-start w-max flex flex-col gap-1 transition-all duration-300 !max-h-[250px] !overflow-auto"
                              } ${isDropdownOpen ? "open" : ""}`}
                          >
                            {item.children.map((child) => {
                              const isChildActive = child.path && matchesRoute(location.pathname, child.path);
                              const childButton = (
                                <Button
                                  component={child.disabled ? "button" : Link}
                                  to={child.disabled ? undefined : child.path}
                                  disabled={Boolean(child.disabled)}
                                  key={child.name}
                                  sx={{
                                    '&:hover': {
                                      backgroundColor: "#1c252e0d",
                                    },
                                    '&.Mui-disabled': {
                                      opacity: 0.45,
                                      color: '#919EAB',
                                      cursor: 'not-allowed',
                                    },
                                  }}
                                  className={`!py-1 flex !justify-start !items-start rounded-[10px] cursor-pointer w-full !mt-1 !text-start text-sm leading-[22px] hover:bg-[#919EAB14] ${isOpen ? "!ps-[48px]" : ""} ${isChildActive
                                    ? "menu-child-active !text-[#118D57] !bg-[#E1F4E5] !font-semibold"
                                    : "!text-[#637381] !font-normal"
                                    }`}
                                  onClick={() => {
                                    if (child.disabled) {
                                      return;
                                    }
                                    setOpenPlanningDropdown(null);
                                  }}
                                >
                                  {child.name}
                                </Button>
                              );

                              if (child.disabled) {
                                return (
                                  <Tooltip key={child.name} title={child.disabledTooltip || "No permission"} placement="right" arrow>
                                    <span>{childButton}</span>
                                  </Tooltip>
                                );
                              }

                              return childButton;
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }

                  if (item.disabled) {
                    const disabledRow = (
                      <div
                        aria-disabled="true"
                        className={`menu-item flex items-center gap-2 ${isOpen ? "justify-start p-1" : "self-start ps-6 py-2"
                          } px-4 rounded-[10px] cursor-not-allowed text-[#C4CDD5] opacity-60`}
                        style={{ pointerEvents: "auto" }}
                        onClick={(e) => e.preventDefault()}
                      >
                        {item.icon}
                        {isOpen && (
                          <span
                            className={`transition-opacity duration-300 text-sm leading-[22px]  ${isOpen
                              ? "opacity-100 translate-x-0"
                              : "opacity-0 -translate-x-4"
                              }`}
                          >
                            {item.name}
                          </span>
                        )}
                      </div>
                    );
                    return (
                      <div key={item.name} className="relative">
                        <Tooltip
                          title={item.disabledTooltip || "No permission"}
                          placement="right"
                          arrow
                        >
                          <span>{disabledRow}</span>
                        </Tooltip>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={item.name}
                      className="relative"
                      onMouseEnter={() => !isOpen && setHoveredItem(`planning-${index}`)}
                      onMouseLeave={() => !isOpen && setHoveredItem(null)}
                    >
                      <Link
                        to={item.path}
                        className={`menu-item flex items-center gap-2 hover:bg-[#919EAB14] ${isOpen ? "justify-start p-1" : "self-start ps-6 py-2"
                          } px-4 rounded-[10px] cursor-pointer ${isActive
                            ? "menu-active text-[#118D57] font-semibold"
                            : "text-[#212B36]"
                            }`}
                      >
                        {item.icon}
                        {isOpen && (
                          <span
                            className={`transition-opacity duration-300 text-sm leading-[22px]  ${isOpen
                              ? "opacity-100 translate-x-0"
                              : "opacity-0 -translate-x-4"
                              }`}
                          >
                            {item.name}
                          </span>
                        )}
                      </Link>
                      {!isOpen && hoveredItem === `planning-${index}` && (
                        <div className="absolute left-[72px] top-0 bg-white/[.98] p-1.5 shadow-lg rounded-lg w-max transition-opacity duration-300">
                          <span className="block p-1 px-2 text-sm leading-[22px] text-[#637381]">{item.name}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className={`${isOpen ? "hidden" : "block"} border border-[#919EAB33] w-[24px] mx-auto my-[32px]`}></div>
            <div className="space-y-1 mb-9">
              {/* Tools Section Header */}
              {isOpen && (
                <div className="pl-4 font-bold uppercase text-xs  bg-gradient-to-r from-[#a4de48] to-[#5ec1b0] inline-block text-transparent bg-clip-text mt-6 mb-2">
                  Tools
                </div>
              )}

              {menuItemsThree.map((item, index) => (
                <div
                  key={item.name}
                  onMouseEnter={() => {
                    if (!isOpen) {
                      setOpenToolsDropdown(index);
                      setHoveredItem(`tools-${index}`);
                    }
                  }}
                  onMouseLeave={() => {
                    if (!isOpen) {
                      setOpenToolsDropdown(null);
                      setHoveredItem(null);
                    }
                  }}
                  className="relative"
                >
                  <div
                    className={`menu-item flex items-center hover:bg-[#919EAB14]   gap-1 ${isOpen ? "justify-between p-1" : "justify-start ps-6 py-2"
                      }   px-4 !rounded-[10px] cursor-pointer ${item.path && matchesRoute(location.pathname, item.path) ? "menu-active text-[#118D57] font-bold" : ""
                      }`}
                    onClick={() => {
                      if (item.name === "View as" && !item.disable) {
                        handleViewAsClick();
                      } else if (item.redirectToPage) {
                        navigate(item.path);
                      } else {
                        toggleToolsDropdown(index);
                      }
                    }}
                    onFocus={() => !isOpen && setOpenToolsDropdown(index)} // Show on hover in collapsed mode
                    onBlur={() => !isOpen && setOpenToolsDropdown(null)} // Hide on hover out
                  >
                    <div className={`flex items-center gap-2 ${item.path && exactMatchRoute(location.pathname, item.path)
                            ? "menu-active text-[#118D57] font-semibold"
                            : "text-[#919EAB]"
                          }`}>
                      {item.icon}

                      {isOpen && (
                        <span
                          className={`transition-opacity duration-300 text-[#212B36] font-normal text-sm leading-[22px] ${isOpen ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
                            }`}
                        >
                          {item.name}
                        </span>
                      )}
                    </div>
                    {/* Show a dropdown icon if the item has children */}
                    {item.children && item.children.length > 0 && (
                      <span className="ml-auto text-[#637381]">
                        {openToolsDropdown === index ? (
                          isOpen ? (
                            <Icon
                              icon="eva:arrow-ios-upward-fill"
                            />
                          ) : (
                            <IoIosArrowForward />
                          )
                        ) : isOpen ? (
                          <Icon
                            icon="eva:arrow-ios-downward-fill"
                          />
                        ) : (
                          <IoIosArrowForward />
                        )}
                      </span>
                    )}
                  </div>

                  {/* Dropdown section for child items */}
                  {openToolsDropdown === index && item.children && item.children.length > 0 && (
                    <div
                      className={`dropdown-menu ${isOpen ? "block transition-all duration-300" : "absolute left-[72px] top-[-22px] bg-white/[.98]  p-2 shadow-lg rounded-lg w-max transition-all duration-300"
                        } ${openToolsDropdown === index ? "open" : ""}`}
                    >
                      {(() => {
                        // Conditionally include the username in the dropdown items
                        const dropdownItems = !isOpen
                          ? [{ name: item.name }, ...item.children] // Show username when menu is collapsed
                          : item.children; // Only show children when menu is open

                        return dropdownItems.map((child, childIndex) => (
                          <div key={`${child.name}-${childIndex}`}>
                            {child.name === "Log out" ? (
                              <div className={`block p-1  mt-1 flex space-x-2 gap-1 rounded-[10px] w-full text-sm leading-[22px] text-[#637381] cursor-pointer hover:bg-[#1c252e0d] ${isOpen ? "!ps-[52px]" : "px-4"}
                              }`}
                              onClick={() => setOpenToolsDropdown(null)}
                              >
                                <LogoutButton />
                              </div>
                            ) : !child.path ? (
                              <div
                                className={`block p-1 mt-1 rounded-[10px] w-full text-sm leading-[22px] text-[#212B36] ${isOpen ? "!ps-[52px]" : "px-4"
                                  }`}
                                style={{
                                  fontSize: "12px",
                                  padding: "0px 16px !important",
                                  lineHeight: "18px",
                                  fontWeight: "600",
                                  color: "#212B36",
                                }}
                              >
                                {child.name}
                              </div>
                            ) : (
                              <Link
                                to={child.path}
                                className={`block p-2.5 rounded-lg cursor-pointer hover:bg-[#1c252e0d] ${child.path && exactMatchRoute(location.pathname, child.path)
                                  ? "menu-child-active text-[#118D57] font-bold"
                                  : ""
                                  }`}
                              >
                                {child.name}
                              </Link>
                            )}
                          </div>
                        ));
                      })()}
                    </div>
                  )}

                  {/* Show tooltip for items without children or when no dropdown is open */}
                  {!isOpen && (item.children?.length ?? 0) === 0 && hoveredItem === `tools-${index}` && (
                    <div className="absolute left-[72px] top-0 bg-white/[.98] p-2 shadow-lg rounded-lg w-max">
                      <span className="block p-1 px-2 text-sm leading-[22px] text-[#637381]">{item.name}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {viewAsDialogOpen && (
        <ViewAsDialog 
          open={viewAsDialogOpen} 
          onClose={() => setViewAsDialogOpen(false)} 
        />
      )}
    </>
  );
};

export default Sidebar;